import React, { useState, useEffect } from "react";
import HeaderMobile from "./HeaderMobile.js";
import Header from "./Header.js";
import Sidebar from "./Sidebar.js";
import Footer from "./Footer.js";
import { BASE_URL } from "./Constants.js";
import * as XLSX from "xlsx";

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // =========================================================
  // DASHBOARD / UNIFORM SUMMARY STATES
  // =========================================================

  const [areaWiseData, setAreaWiseData] = useState([]);
  const [noDataFound, setNoDataFound] = useState(false);

  const [approvedUniformsIssued, setApprovedUniformsIssued] = useState(0);
  const [approvedUniformsCost, setApprovedUniformsCost] = useState(0);

  const [pendingUniformsIssued, setPendingUniformsIssued] = useState(0);
  const [pendingUniformsCost, setPendingUniformsCost] = useState(0);

  const [error, setError] = useState(null);

  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);

  const [genderFilter, setGenderFilter] = useState("");
  const [dressTypeFilter, setDressTypeFilter] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedArea, setSelectedArea] = useState("");

  const [sizeFilter, setSizeFilter] = useState("");
  const [colorFilter, setColorFilter] = useState("");

  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // =========================================================
  // DASHBOARD DATE FILTER
  // =========================================================

  const getDefaultDates = () => {
    const now = new Date();

    const firstDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      2
    );

    const lastDay = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0
    );

    const format = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    return {
      from: format(firstDay),
      to: format(lastDay),
    };
  };

  const defaultDates = getDefaultDates();

  const [fromDate, setFromDate] = useState(defaultDates.from);
  const [toDate, setToDate] = useState(defaultDates.to);

  // =========================================================
  // STOCK BALANCE REPORT STATES
  // =========================================================

  const [stockData, setStockData] = useState([]);

  const [stockSearchQuery, setStockSearchQuery] = useState("");
  const [stockAreaFilter, setStockAreaFilter] = useState("");

  const [stockFromDate, setStockFromDate] = useState("");
  const [stockToDate, setStockToDate] = useState("");

  const [stockCurrentPage, setStockCurrentPage] = useState(1);
  const [stockItemsPerPage, setStockItemsPerPage] = useState(10);

  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState(null);

  // =========================================================
  // SIDEBAR
  // =========================================================

  const handleToggleSidebar = () => {
    if (window.innerWidth >= 992) {
      setIsSidebarOpen((prev) => !prev);
    }
  };

  // =========================================================
  // FETCH DASHBOARD APPROVED / PENDING DATA
  // =========================================================

  const handleFilter = async () => {
    if (!fromDate || !toDate) {
      alert("Please select both From and To dates.");
      return;
    }

    const token = localStorage.getItem("authToken");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const params = `?from_date=${fromDate}&to_date=${toDate}${
      selectedLocation && selectedLocation !== "All"
        ? `&area=${selectedLocation}`
        : ""
    }`;

    try {
      setLoading(true);
      setError(null);

      // Approved uniforms
      const approvedRes = await fetch(
        `${BASE_URL}total_approved_uniforms_issued_by_user${params}`,
        {
          headers,
        }
      );

      const approvedData = await approvedRes.json();

      if (approvedData.status) {
        setApprovedUniformsIssued(
          approvedData.data.total_issued || 0
        );

        setApprovedUniformsCost(
          approvedData.data.total_cost || 0
        );
      } else {
        setApprovedUniformsIssued(0);
        setApprovedUniformsCost(0);
      }

      // Pending uniforms
      const pendingRes = await fetch(
        `${BASE_URL}total_pending_uniforms_issued_by_user${params}`,
        {
          headers,
        }
      );

      const pendingData = await pendingRes.json();

      if (pendingData.status) {
        setPendingUniformsIssued(
          pendingData.data.total_issued || 0
        );

        setPendingUniformsCost(
          pendingData.data.total_cost || 0
        );
      } else {
        setPendingUniformsIssued(0);
        setPendingUniformsCost(0);
      }
    } catch (err) {
      console.error("Dashboard filter error:", err);
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FETCH LOCATIONS
  // =========================================================

  const fetchLocations = async () => {
    const token = localStorage.getItem("authToken");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    try {
      const res = await fetch(`${BASE_URL}get_locations`, {
        headers,
      });

      const data = await res.json();

      if (data.status && Array.isArray(data.data)) {
        setLocations(data.data);
      }
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  };

  // =========================================================
  // FETCH AREAS
  // =========================================================

  const fetchAreas = async () => {
    const token = localStorage.getItem("authToken");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    try {
      const res = await fetch(`${BASE_URL}get_areas`, {
        headers,
      });

      const data = await res.json();

      if (data.status && Array.isArray(data.data)) {
        setAreas(data.data);
      }
    } catch (err) {
      console.error("Error fetching areas:", err);
    }
  };

  // =========================================================
  // FETCH AREA-WISE UNIFORM SUMMARY
  // =========================================================

  const fetchAreaWiseData = async (areaId = "") => {
    const token = localStorage.getItem("authToken");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    let url = `${BASE_URL}area_wise_uniform_summary`;

    if (areaId) {
      url += `?area=${areaId}`;
    }

    try {
      const res = await fetch(url, {
        headers,
      });

      const data = await res.json();

      if (data.status && Array.isArray(data.data)) {
        setAreaWiseData(data.data);
        setNoDataFound(false);
      } else {
        setAreaWiseData([]);
        setNoDataFound(true);
      }
    } catch (err) {
      console.error("Error fetching area-wise stats:", err);

      setAreaWiseData([]);
      setNoDataFound(true);
    }
  };

  // =========================================================
  // FETCH STOCK BALANCE REPORT
  // =========================================================

  const fetchStockReport = async () => {
    setStockLoading(true);
    setStockError(null);

    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(
        `${BASE_URL}overall_stock_balance_report`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await res.json();

      if (data.status && Array.isArray(data.data)) {
        setStockData(data.data);
      } else {
        setStockData([]);
        setStockError("No stock data found");
      }
    } catch (err) {
      console.error("Stock report error:", err);

      setStockData([]);
      setStockError(
        err.message || "Failed to fetch stock data"
      );
    } finally {
      setStockLoading(false);
    }
  };

  // =========================================================
  // INITIAL API CALLS
  // =========================================================

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchAreas();
  }, []);

  useEffect(() => {
    handleFilter();
  }, []);

  useEffect(() => {
    fetchAreaWiseData(selectedArea);
  }, [selectedArea]);

  useEffect(() => {
    fetchStockReport();
  }, []);

  // =========================================================
  // AREA-WISE FILTERS
  // =========================================================

  const uniqueSizes = [
    ...new Set(
      areaWiseData.map(
        (item) => item.dress_size_name
      )
    ),
  ];

  const uniqueColors = [
    ...new Set(
      areaWiseData.map(
        (item) => item.dress_color_name
      )
    ),
  ];

  const sortedSizes = uniqueSizes.sort();
  const sortedColors = uniqueColors.sort();

  const filteredData = areaWiseData.filter(
    (item) =>
      (genderFilter === "" ||
        item.gender === genderFilter) &&
      (dressTypeFilter === "" ||
        item.dress_type_name === dressTypeFilter) &&
      (sizeFilter === "" ||
        item.dress_size_name === sizeFilter) &&
      (colorFilter === "" ||
        item.dress_color_name === colorFilter)
  );

  // =========================================================
  // AREA-WISE PAGINATION
  // =========================================================

  const indexOfLastItem =
    currentPage * itemsPerPage;

  const indexOfFirstItem =
    indexOfLastItem - itemsPerPage;

  const currentItems = filteredData.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil(
    filteredData.length / itemsPerPage
  );

  // =========================================================
  // AREA-WISE EXCEL EXPORT
  // =========================================================

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      alert("No filtered data available to export.");
      return;
    }

    const worksheetData = filteredData.map(
      (item, index) => ({
        "#": index + 1,
        Area: item.area_name,
        Gender: item.gender,
        "Dress Type": item.dress_type_name,
        Size: item.dress_size_name,
        Color: item.dress_color_name,
        Quantity: item.quantity,
      })
    );

    const worksheet =
      XLSX.utils.json_to_sheet(worksheetData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Uniform Summary"
    );

    XLSX.writeFile(
      workbook,
      "Filtered_Uniform_Summary_Report.xlsx"
    );
  };

  // =========================================================
  // STOCK FILTERING
  // =========================================================

  const filteredStock = stockData.filter((item) => {
    const searchMatch = Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(
        stockSearchQuery.toLowerCase()
      );

    const areaMatch =
      !stockAreaFilter ||
      String(item.area_id) ===
        String(stockAreaFilter);

    const itemDate = item.delivery_given_date
      ? new Date(item.delivery_given_date)
      : null;

    const fromDateMatch = stockFromDate
      ? itemDate &&
        itemDate >=
          new Date(
            `${stockFromDate}T00:00:00`
          )
      : true;

    const toDateMatch = stockToDate
      ? itemDate &&
        itemDate <=
          new Date(
            `${stockToDate}T23:59:59`
          )
      : true;

    return (
      searchMatch &&
      areaMatch &&
      fromDateMatch &&
      toDateMatch
    );
  });

  // =========================================================
  // STOCK PAGINATION
  // =========================================================

  const stockIndexOfLast =
    stockCurrentPage *
    stockItemsPerPage;

  const stockIndexOfFirst =
    stockIndexOfLast -
    stockItemsPerPage;

  const currentStock =
    filteredStock.slice(
      stockIndexOfFirst,
      stockIndexOfLast
    );

  const stockTotalPages =
    Math.ceil(
      filteredStock.length /
        stockItemsPerPage
    );

  // =========================================================
  // STOCK AREA LIST
  // =========================================================

  const stockAreaList = [
    ...new Map(
      stockData.map((item) => [
        item.area_id,
        item,
      ])
    ).values(),
  ];

  // =========================================================
  // STOCK EXCEL EXPORT
  // =========================================================

  const handleStockExcelExport = () => {
    if (filteredStock.length === 0) {
      alert(
        "No stock data available to export."
      );
      return;
    }

    const exportData = filteredStock.map(
      (item) => ({
        Area: item.area_name,
        "Dress Type":
          item.dress_type_name,
        Size: item.dress_size_name,
        Color:
          item.dress_color_name,
        Gender: item.gender,
        Received:
          item.received_stock,
        Issued:
          item.issued_stock,
        Pending:
          item.pending_issue,
        "Total Sales":
          item.total_sale,
        Physical:
          item.physical_stock,
        Balance:
          item.balance_purchase_report,
      })
    );

    const worksheet =
      XLSX.utils.json_to_sheet(exportData);

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Stock Report"
    );

    XLSX.writeFile(
      workbook,
      "Stock_Balance_Report.xlsx"
    );
  };

  // =========================================================
  // RESET STOCK FILTERS
  // =========================================================

  const clearStockFilters = () => {
    setStockSearchQuery("");
    setStockAreaFilter("");
    setStockFromDate("");
    setStockToDate("");
    setStockCurrentPage(1);
  };

  // =========================================================
  // RESET AREA-WISE FILTERS
  // =========================================================

  const clearAreaFilters = () => {
    setSelectedArea("");
    setGenderFilter("");
    setDressTypeFilter("");
    setSizeFilter("");
    setColorFilter("");
    setCurrentPage(1);
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="page-wrapper d-flex flex-column min-vh-100">

      <div className="flex-grow-1">

        <HeaderMobile />

        <Sidebar
          isOpen={isSidebarOpen}
        />

        <div
          className="page-container"
          style={{
            marginLeft:
              isSidebarOpen &&
              window.innerWidth >= 992
                ? "300px"
                : "100px",
            transition:
              "margin-left 0.3s ease",
          }}
        >

          <Header
            onToggleSidebar={
              handleToggleSidebar
            }
            isSidebarOpen={
              isSidebarOpen
            }
          />

          <div className="main-content">

            <div className="section__content section__content--p30">

              <div className="container-fluid">

                {/* =====================================================
                    DASHBOARD DATE / LOCATION FILTER
                ====================================================== */}

                <div className="row mb-4">

                  <div className="col-md-3 mb-2">
                    <label>
                      From Date
                    </label>

                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) =>
                        setFromDate(
                          e.target.value
                        )
                      }
                    />
                  </div>

                  <div className="col-md-3 mb-2">
                    <label>
                      To Date
                    </label>

                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={(e) =>
                        setToDate(
                          e.target.value
                        )
                      }
                    />
                  </div>

                  <div className="col-md-3 mb-2">

                    <label>
                      Location
                    </label>

                    <select
                      className="form-control"
                      value={
                        selectedLocation
                      }
                      onChange={(e) =>
                        setSelectedLocation(
                          e.target.value
                        )
                      }
                    >

                      <option value="All">
                        All Locations
                      </option>

                      {areas.map(
                        (loc, idx) => (
                          <option
                            key={idx}
                            value={loc.id}
                          >
                            {loc.area}
                          </option>
                        )
                      )}

                    </select>

                  </div>

                  <div className="col-md-2 mb-2 d-flex align-items-end">

                    <button
                      className="btn btn-primary"
                      onClick={
                        handleFilter
                      }
                    >
                      Apply Filter
                    </button>

                  </div>

                </div>

                {/* =====================================================
                    STATISTICS
                ====================================================== */}

                <div className="row m-t-25">

                  <div className="col-sm-12 col-lg-3 mb-3">

                    <div className="overview-item overview-item--c1">

                      <div className="overview__inner">

                        <div className="overview-box clearfix">

                          <div className="text">

                            <h2>
                              {
                                approvedUniformsIssued
                              }
                            </h2>

                            <span>
                              Total Approved Uniforms Issued
                            </span>

                          </div>

                        </div>

                      </div>

                    </div>

                  </div>

                  <div className="col-sm-6 col-lg-3 mb-3">

                    <div className="overview-item overview-item--c2">

                      <div className="overview__inner">

                        <div className="overview-box clearfix">

                          <div className="text">

                            <h2>
                              {
                                approvedUniformsCost
                              }
                            </h2>

                            <span>
                              Total Approved Uniforms Amount
                            </span>

                          </div>

                        </div>

                      </div>

                    </div>

                  </div>

                  <div className="col-sm-6 col-lg-3 mb-3">

                    <div className="overview-item overview-item--c3">

                      <div className="overview__inner">

                        <div className="overview-box clearfix">

                          <div className="text">

                            <h2>
                              {
                                pendingUniformsIssued
                              }
                            </h2>

                            <span>
                              Total Pending Uniforms Issued
                            </span>

                          </div>

                        </div>

                      </div>

                    </div>

                  </div>

                  <div className="col-sm-6 col-lg-3 mb-3">

                    <div className="overview-item overview-item--c4">

                      <div className="overview__inner">

                        <div className="overview-box clearfix">

                          <div className="text">

                            <h2>
                              {
                                pendingUniformsCost ||
                                0
                              }
                            </h2>

                            <span>
                              Total Pending Uniforms Amount
                            </span>

                          </div>

                        </div>

                      </div>

                    </div>

                  </div>

                </div>

               

                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">

                  <h3 className="mb-2">
                    Stock Balance Report
                  </h3>

                  <button
                    className="btn btn-success mb-2"
                    onClick={
                      handleStockExcelExport
                    }
                  >
                    Export Stock Excel
                  </button>

                </div>

                {/* STOCK FILTERS */}

                <div className="row mb-3">

                  <div className="col-md-3 mb-2">

                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search stock..."
                      value={
                        stockSearchQuery
                      }
                      onChange={(e) => {
                        setStockSearchQuery(
                          e.target.value
                        );
                        setStockCurrentPage(
                          1
                        );
                      }}
                    />

                  </div>

                  <div className="col-md-2 mb-2">

                    <select
                      className="form-control"
                      value={
                        stockAreaFilter
                      }
                      onChange={(e) => {
                        setStockAreaFilter(
                          e.target.value
                        );
                        setStockCurrentPage(
                          1
                        );
                      }}
                    >

                      <option value="">
                        All Areas
                      </option>

                      {stockAreaList.map(
                        (area, index) => (

                          <option
                            key={index}
                            value={
                              area.area_id
                            }
                          >
                            {
                              area.area_name
                            }
                          </option>

                        )
                      )}

                    </select>

                  </div>

                  <div className="col-md-2 mb-2">

                    <input
                      type="date"
                      className="form-control"
                      value={
                        stockFromDate
                      }
                      onChange={(e) => {
                        setStockFromDate(
                          e.target.value
                        );
                        setStockCurrentPage(
                          1
                        );
                      }}
                    />

                  </div>

                  <div className="col-md-2 mb-2">

                    <input
                      type="date"
                      className="form-control"
                      value={
                        stockToDate
                      }
                      onChange={(e) => {
                        setStockToDate(
                          e.target.value
                        );
                        setStockCurrentPage(
                          1
                        );
                      }}
                    />

                  </div>

                  <div className="col-md-1 mb-2">

                    <select
                      className="form-control"
                      value={
                        stockItemsPerPage
                      }
                      onChange={(e) => {
                        setStockItemsPerPage(
                          Number(
                            e.target.value
                          )
                        );

                        setStockCurrentPage(
                          1
                        );
                      }}
                    >

                      <option value={10}>
                        10
                      </option>

                      <option value={25}>
                        25
                      </option>

                      <option value={50}>
                        50
                      </option>

                    </select>

                  </div>

                  <div className="col-md-2 mb-2">

                    <button
                      className="btn btn-secondary"
                      onClick={
                        clearStockFilters
                      }
                    >
                      Clear Filters
                    </button>

                  </div>

                </div>

                {/* STOCK TABLE */}

                {stockLoading ? (

                  <div className="text-center mt-4">

                    <div
                      className="spinner-border text-primary"
                      role="status"
                    >
                      <span className="sr-only">
                        Loading...
                      </span>
                    </div>

                    <div className="mt-2">
                      Loading stock data...
                    </div>

                  </div>

                ) : stockError ? (

                  <div className="alert alert-danger mt-4">

                    <strong>
                      Stock Report Error:
                    </strong>{" "}
                    {stockError}

                  </div>

                ) : (

                  <>

                    <div className="table-responsive mt-3">

                      <table className="table table-bordered table-striped">

                        <thead>

                          <tr>

                            <th>#</th>
                            <th>Area</th>
                            <th>Dress Type</th>
                            <th>Size</th>
                            <th>Color</th>
                            <th>Gender</th>
                            <th>Received</th>
                            <th>Issued</th>
                            <th>Pending</th>
                            <th>Total Sales</th>
                            <th>Physical</th>
                            <th>Balance</th>

                          </tr>

                        </thead>

                        <tbody>

                          {currentStock.length >
                          0 ? (

                            currentStock.map(
                              (
                                item,
                                index
                              ) => (

                                <tr
                                  key={`${item.area_id}-${item.dress_type_name}-${item.dress_size_name}-${index}`}
                                >

                                  <td>
                                    {
                                      stockIndexOfFirst +
                                      index +
                                      1
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.area_name
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.dress_type_name
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.dress_size_name
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.dress_color_name
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.gender
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.received_stock
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.issued_stock
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.pending_issue
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.total_sale
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.physical_stock
                                    }
                                  </td>

                                  <td>
                                    {
                                      item.balance_purchase_report
                                    }
                                  </td>

                                </tr>

                              )
                            )

                          ) : (

                            <tr>

                              <td
                                colSpan="12"
                                className="text-center"
                              >
                                No stock data available
                              </td>

                            </tr>

                          )}

                        </tbody>

                      </table>

                    </div>

                    {/* STOCK PAGINATION */}

                    <div
                      className="d-flex justify-content-between align-items-center mt-3 flex-wrap"
                      style={{
                        gap: "10px",
                      }}
                    >

                      <div>

                        Showing{" "}

                        <strong>
                          {filteredStock.length >
                          0
                            ? stockIndexOfFirst +
                              1
                            : 0}
                        </strong>{" "}

                        to{" "}

                        <strong>
                          {Math.min(
                            stockIndexOfLast,
                            filteredStock.length
                          )}
                        </strong>{" "}

                        of{" "}

                        <strong>
                          {
                            filteredStock.length
                          }
                        </strong>{" "}
                        entries

                      </div>

                      <nav>

                        <ul className="pagination mb-0">

                          <li
                            className={`page-item ${
                              stockCurrentPage ===
                              1
                                ? "disabled"
                                : ""
                            }`}
                          >

                            <button
                              className="page-link"
                              onClick={() =>
                                setStockCurrentPage(
                                  (prev) =>
                                    Math.max(
                                      prev - 1,
                                      1
                                    )
                                )
                              }
                            >
                              &lt;
                            </button>

                          </li>

                          {Array.from(
                            {
                              length:
                                stockTotalPages,
                            },
                            (_, i) =>
                              i + 1
                          )
                            .filter(
                              (p) =>
                                p === 1 ||
                                p ===
                                  stockTotalPages ||
                                (p >=
                                  stockCurrentPage -
                                    2 &&
                                  p <=
                                    stockCurrentPage +
                                      2)
                            )
                            .map(
                              (
                                pageNum,
                                index,
                                array
                              ) => {

                                const prevPage =
                                  array[
                                    index - 1
                                  ];

                                const isEllipsis =
                                  prevPage &&
                                  pageNum -
                                    prevPage >
                                    1;

                                return (
                                  <React.Fragment
                                    key={
                                      pageNum
                                    }
                                  >

                                    {isEllipsis && (
                                      <li className="page-item disabled">

                                        <span className="page-link">
                                          ...
                                        </span>

                                      </li>
                                    )}

                                    <li
                                      className={`page-item ${
                                        stockCurrentPage ===
                                        pageNum
                                          ? "active"
                                          : ""
                                      }`}
                                    >

                                      <button
                                        className="page-link"
                                        onClick={() =>
                                          setStockCurrentPage(
                                            pageNum
                                          )
                                        }
                                      >
                                        {
                                          pageNum
                                        }
                                      </button>

                                    </li>

                                  </React.Fragment>
                                );
                              }
                            )}

                          <li
                            className={`page-item ${
                              stockCurrentPage ===
                                stockTotalPages ||
                              stockTotalPages ===
                                0
                                ? "disabled"
                                : ""
                            }`}
                          >

                            <button
                              className="page-link"
                              onClick={() =>
                                setStockCurrentPage(
                                  (prev) =>
                                    Math.min(
                                      prev + 1,
                                      stockTotalPages
                                    )
                                )
                              }
                            >
                              &gt;
                            </button>

                          </li>

                        </ul>

                      </nav>

                    </div>

                  </>

                )}

              </div>

            </div>

          </div>

        </div>

      </div>

      <Footer />

    </div>
  );
};

export default Dashboard;
